import { useEffect, useRef } from "react";

import type { FileItem } from "../api/client";
import { ThumbnailTile } from "./ThumbnailTile";

type Props = {
  items: FileItem[];
  hasNextPage: boolean;
  onLoadMore: () => void;
  selectMode?: boolean;
  selectedIds?: Set<string>;
  onTileClick?: (file: FileItem) => void;
  onTileLongPress?: (file: FileItem) => void;
  renderTileOverlay?: (file: FileItem) => JSX.Element | null;
};

export function ThumbnailGrid({
  items,
  hasNextPage,
  onLoadMore,
  selectMode,
  selectedIds,
  onTileClick,
  onTileLongPress,
  renderTileOverlay,
}: Props) {
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!sentinel.current || !hasNextPage) return;
    const el = sentinel.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) onLoadMore();
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, onLoadMore]);

  return (
    <div className="p-1">
      <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-1">
        {items.map((f) => (
          <ThumbnailTile
            key={f.id}
            file={f}
            selectMode={selectMode}
            selected={selectedIds?.has(f.id)}
            onClick={onTileClick}
            onLongPress={onTileLongPress}
            rightOverlay={renderTileOverlay?.(f) ?? null}
          />
        ))}
      </div>
      <div ref={sentinel} className="h-8" />
    </div>
  );
}
